import PortalDocument, {PortalDocumentInterface} from "./PortalDocument";
import {
	AbstractDocumentManager,
	HttpMethod,
	RestSession,
	WebServiceType
} from "@softvision/webpdf-wsclient-9-typescript";
import {Errors, Result, ResultException} from "@softvision/webpdf-wsclient-9-typescript/lib/exception";
import PortalSession from "../session/PortalSession";
import DocumentSettings, {DocumentSettingsInterface} from "./DocumentSettings";
import ContentMode from "../ContentMode";
import {PortalState} from "../Portal";
import {HttpRestRequestFactory} from "@softvision/webpdf-wsclient-9-typescript/lib/http/HttpRestRequestFactory";
import {HttpRestResponse} from "@softvision/webpdf-wsclient-9-typescript/lib/http/HttpRestResponse";
import SidebarRightMode from "../viewer/settings/SidebarRightMode";
import PortalContext from "../PortalContext";
import Message from "../../shared/log/Message";
import {MessageType} from "../../shared/log/MessageInterface";
import {
	DocumentFile,
	HistoryEntry,
	InfoForm,
	InfoType,
	PdfPassword
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import i18next from "i18next";
import Immutable from "../../shared/immutable/Immutable";
import PageSelection from "./PageSelection";
import ViewerSettings from "./ViewerSettings";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import Exception from "../../shared/exception/Exception";

export interface PortalDocumentManagerGlobals {
	portalDocuments: Immutable<Array<PortalDocumentInterface>>;
	documentSelection: Immutable<Array<PortalDocumentInterface>>;
}

export function initPortalDocumentManagerGlobals(): PortalDocumentManagerGlobals {
	return {
		portalDocuments: Immutable.from([]),
		documentSelection: Immutable.from([])
	};
}

export default class PortalDocumentManager extends AbstractDocumentManager<PortalDocument> {
	private _context: PortalContext;

	/**
	 * Constructs a {@link PortalDocumentManager} for the given {@link RestSession}.
	 */
	constructor(session: RestSession<PortalDocument>) {
		super(session);

		this._context = new PortalContext();
		this.setUseHistory(true);
	}

	private async updateDocumentFormInfo(documentId: string): Promise<void> {
		let portalDocument: PortalDocument = this.findDocument(documentId);
		if (!portalDocument) {
			throw new ResultException(Result.build(Errors.INVALID_FILE_SOURCE));
		}

		if (!FunctionHelper.exists(portalDocument.metadata?.form)) {
			return;
		}

		let formInfo: InfoForm = await this.fetchDocumentInfo(documentId, InfoType.Form);

		if (formInfo.error?.errorCode !== 0) {
			let exception: Exception = Exception.parseExceptionBean(formInfo.error!);
			portalDocument.setExecutionError(exception);
		} else {
			let settings: DocumentSettings = portalDocument.getSettings();
			settings.formInfo = formInfo;
		}

		this.updateGlobalDocumentList();
	}

	/**
	 * Uploads the given {@link File | Blob} to the webPDF server and returns the {@link PortalDocument} reference
	 * to the uploaded resource.
	 */
	public async uploadDocument(file?: File | Blob, options?: {
		onProgress?: (event: any) => void;
		filename?: string;
		id?: string;
	}): Promise<PortalDocument> {
		let uploadedDocument: PortalDocument = await super.uploadDocument(file, options);
		this.updateDocumentFormInfo(uploadedDocument.getSourceDocumentId()!);

		if (uploadedDocument.hasHtmlPreview()) {
			await this.downloadRawData(uploadedDocument.getSourceDocumentId()!);
		} else {
			uploadedDocument.data = await file?.arrayBuffer();
		}

		if (this.isUseHistory()) {
			let historyEntry: HistoryEntry = uploadedDocument.lastHistory();
			await this.updateHistoryOperation(
				uploadedDocument.getSourceDocumentId()!, historyEntry.id!,
				JSON.stringify({func: "DOCUMENT:UPLOAD", options: ""})
			);
		}

		let message: Message = new Message("success.document.upload", MessageType.SUCCESS);
		message.context = {filename: '"' + uploadedDocument.getFileName() + '" '};
		message.visible = false;
		this._context.getPage().addMessage(message);
		this.updateGlobalDocumentList();
		return uploadedDocument;
	}

	/**
	 * Uploads the given {@link FileList} to the webPDF server.
	 */
	public async uploadDocuments(files: FileList, options?: {
		onProgress?: (event: any) => void,
		abortSignal?: AbortSignal,
		open?: boolean
	}): Promise<void> {
		this.clearSelection();
		let uploadedDocument: PortalDocument | undefined;

		for (let file of files) {
			if (FunctionHelper.exists(options?.abortSignal) && options!.abortSignal!.aborted) {
				break;
			}

			let uploadOptions: any = {
				id: (file as any).id,
				filename: file.name,
				...options
			}

			uploadedDocument = await super.uploadDocument(file, uploadOptions);
			this.updateDocumentFormInfo(uploadedDocument.getSourceDocumentId()!);

			let settings: DocumentSettings = uploadedDocument.getSettings();
			settings.selected = true;

			if (FunctionHelper.exists(options?.open) && options!.open) {
				settings.closed = false;
			}

			if (uploadedDocument.hasHtmlPreview()) {
				await this.downloadRawData(uploadedDocument.getSourceDocumentId()!);
			} else {
				uploadedDocument.data = await file?.arrayBuffer();
			}

			if (this.isUseHistory()) {
				let historyEntry: HistoryEntry = uploadedDocument.lastHistory();
				await this.updateHistoryOperation(
					uploadedDocument.getSourceDocumentId()!, historyEntry.id!,
					JSON.stringify({func: "DOCUMENT:UPLOAD", options: ""})
				);
			}

			let message: Message = new Message("success.document.upload", MessageType.SUCCESS);
			message.context = {filename: '"' + uploadedDocument.getFileName() + '" '};
			message.visible = false;
			this._context.getPage().addMessage(message);
		}

		if (FunctionHelper.exists(uploadedDocument) && FunctionHelper.exists(options?.open) && options!.open) {
			this.open(uploadedDocument!.getSourceDocumentId()!);
		}

		this.updateGlobalDocumentList();
	}

	/**
	 * Activates a history entry for the document
	 */
	public async activateHistory(documentId: string, historyId: number): Promise<PortalDocument> {
		let portalDocument: PortalDocument = await super.activateHistory(documentId, historyId);
		portalDocument.lock();

		let documentSettings: DocumentSettings = portalDocument.getSettings();
		documentSettings.selected = true;
		documentSettings.sidebarRightMode = SidebarRightMode.HISTORY;
		documentSettings.resetMouseMode();
		documentSettings.closed = false;
		this.updateGlobalDocumentList();

		await this.downloadRawData(documentId);
		portalDocument.unlock();
		this.updateGlobalDocumentList();

		return portalDocument;
	}

	/**
	 * Downloads a document
	 */
	public async downloadDocument(documentId: string, options?: {
		id?: string; format?: string; onProgress?: (event: any) => void;
	}): Promise<HttpRestResponse> {
		let portalDocument: PortalDocument = this.findDocument(documentId);
		if (!portalDocument) {
			throw new ResultException(Result.build(Errors.INVALID_FILE_SOURCE));
		}

		let urlPath: string = "documents/" + documentId;
		let additionalParameters: URLSearchParams = new URLSearchParams();

		if (FunctionHelper.exists(options?.format)) {
			additionalParameters.append("format", options!.format!);
		}

		if (portalDocument.hasPasswordProtection("open") ||
			portalDocument.hasCertificateProtection()) {
			additionalParameters.append("decrypt", "true");
		}

		if (additionalParameters.toString() !== "") {
			urlPath += "?" + additionalParameters;
		}

		let additionalHeaders = new Headers();
		let date = new Date(0).toUTCString();
		additionalHeaders.append("If-Modified-Since", date);
		additionalHeaders.append("Accept-Language", i18next.language);

		return await HttpRestRequestFactory.createInstance(this.getSession())
			.setAcceptHeader("application/octet-stream")
			.buildApiRequest({
				httpMethod: HttpMethod.GET,
				path: urlPath,
				additionalHeaders: additionalHeaders,
				onDownloadProgress: (progressEvent: any) => {
					if (FunctionHelper.exists(options?.onProgress)) {
						let event: any = {};

						if (FunctionHelper.exists(options?.id)) {
							event.id = options!.id
						}

						options!.onProgress!({...event, ...progressEvent});
					}
				}
			})
			.execute();
	}

	/**
	 * unselects a {@link PortalDocument} by documentId
	 */
	public unselect(documentId: string, update?: boolean): void {
		let portalDocument: PortalDocument = this.findDocument(documentId);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		if (!documentSettings.selected) {
			return;
		}

		documentSettings.selected = false;

		if (!FunctionHelper.exists(update) || update) {
			let pageUpdate: Partial<PortalState> = {};

			if (this.getSelection().length !== 1) {
				pageUpdate.contentMode = ContentMode.STARTSCREEN;
			}

			this.updateGlobalDocumentList(pageUpdate);
		}
	}

	public open(documentId: string): void {
		for (let document of this.getDocuments()) {
			let documentSettings: DocumentSettings = document.getSettings();

			if (documentSettings.selected) {
				documentSettings.selected = false;
			}
		}

		let portalDocument: PortalDocument = this.findDocument(documentId);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		documentSettings.selected = true;
		documentSettings.closed = false;

		this.updateGlobalDocumentList({
			contentMode: ContentMode.VIEWER
		});
	}

	/**
	 * selects a {@link PortalDocument} by documentId
	 */
	public select(documentId: string, clear?: boolean, update?: boolean): void {
		if (FunctionHelper.exists(clear) && clear) {
			for (let document of this.getDocuments()) {
				let documentSettings: DocumentSettings = document.getSettings();

				if (documentSettings.selected) {
					documentSettings.selected = false;
				}
			}
		}

		let portalDocument: PortalDocument = this.findDocument(documentId);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		if (documentSettings.selected) {
			return;
		}

		documentSettings.selected = true;

		if (!FunctionHelper.exists(update) || update) {
			let pageUpdate: Partial<PortalState> = {};

			if (this.getSelection().length !== 1) {
				pageUpdate.contentMode = ContentMode.STARTSCREEN;
			}

			this.updateGlobalDocumentList(pageUpdate);
		}
	}

	/**
	 * selects all {@link PortalDocument}
	 */
	public selectAll(group?: string): void {
		if (this.getSelection().length === this.getDocuments().length) {
			return;
		}

		for (let portalDocument of this.getDocuments()) {
			if (FunctionHelper.exists(group) && portalDocument.getFileTypeGroup() !== group) {
				continue;
			}

			let documentSettings: DocumentSettings = portalDocument.getSettings();
			documentSettings.selected = true;
		}

		let pageUpdate: Partial<PortalState> = {};

		if (this.getSelection().length !== 1) {
			pageUpdate.contentMode = ContentMode.STARTSCREEN;
		}

		this.updateGlobalDocumentList(pageUpdate);
	}

	/**
	 * clears the current selection
	 */
	public clearSelection(group?: string): void {
		if (this.getSelection().length === 0) {
			return;
		}

		for (let portalDocument of this.getDocuments()) {
			if (FunctionHelper.exists(group) && portalDocument.getFileTypeGroup() !== group) {
				continue;
			}

			let documentSettings: DocumentSettings = portalDocument.getSettings();

			if (!documentSettings.selected) {
				continue;
			}

			documentSettings.selected = false;
		}

		this.updateGlobalDocumentList();
	}

	/**
	 * returns an the currently selected {@link Array<PortalDocument>}
	 */
	public getSelection(): Array<PortalDocument> {
		let selection: Array<PortalDocument> = [];

		for (let portalDocument of this.getDocuments()) {
			let documentSettings: DocumentSettings = portalDocument.getSettings();

			if (documentSettings.selected) {
				selection.push(portalDocument);
			}
		}

		return selection;
	}

	public async processDocument(
		documentId: string, webServiceType: WebServiceType, payload?: BodyInit, additionalHeaders?: Headers,
		additionalParameters?: URLSearchParams
	): Promise<PortalDocument> {
		let originalDocument: PortalDocumentInterface | undefined;
		let portalDocument: PortalDocument | undefined;

		if (documentId === "new") {
			additionalParameters!.set("history", String(this.isUseHistory()));
		} else {
			portalDocument = this.findDocument(documentId);
			originalDocument = portalDocument.toJson();
			portalDocument.lock();
			this.updateGlobalDocumentList();
		}

		try {
			portalDocument = await super.processDocument(
				documentId, webServiceType, payload, additionalHeaders, additionalParameters
			);

			portalDocument.unlock();
			let processedDocumentSettings: DocumentSettings = portalDocument.getSettings();

			// new document created? Unselect the old one
			if (documentId === "new") {
				this.clearSelection();
				processedDocumentSettings.closed = false;
			}

			processedDocumentSettings.selected = true;

			if (FunctionHelper.exists(originalDocument)) {
				let pageCount: number = await portalDocument.getNumberOfPages();

				processedDocumentSettings.pageSelection = new PageSelection();
				for (let page of originalDocument!.settings.pageSelection.selection) {
					processedDocumentSettings.pageSelection.selectPage(page, pageCount);
				}

				processedDocumentSettings.sidebarLeftMode = originalDocument!.settings.sidebarLeftMode;
				processedDocumentSettings.sidebarRightMode = originalDocument!.settings.sidebarRightMode;
				processedDocumentSettings.closed = originalDocument!.settings.closed;
				processedDocumentSettings.editArchiveConfirmation = originalDocument!.settings.editArchiveConfirmation;
				processedDocumentSettings.attachmentPreview = originalDocument!.settings.attachmentPreview;
				processedDocumentSettings.previewZoomValue = originalDocument!.settings.previewZoomValue;

				processedDocumentSettings.viewerSettings = ViewerSettings.fromJson(
					originalDocument!.settings.viewerSettings
				);
				if (processedDocumentSettings.viewerSettings.currentPage > pageCount) {
					processedDocumentSettings.viewerSettings.currentPage = pageCount;
				}

				processedDocumentSettings.portfolioSelection = originalDocument!.settings.portfolioSelection;
				processedDocumentSettings.portfolioFolder = originalDocument!.settings.portfolioFolder;
				processedDocumentSettings.hideFormMessage = originalDocument!.settings.hideFormMessage;

				// new document created? Unselect and unlock the old one
				if (documentId !== portalDocument.getSourceDocumentId()) {
					let parentDocument: PortalDocument = this.findDocument(documentId);
					this.unselect(documentId, false);
					parentDocument.unlock();
				}
			}

			await this.downloadRawData(portalDocument.getSourceDocumentId()!);
			this.updateDocumentFormInfo(portalDocument.getSourceDocumentId()!);

			if (this.isUseHistory()) {
				let historyEntry: HistoryEntry = portalDocument.lastHistory();
				await this.updateHistoryOperation(
					portalDocument.getSourceDocumentId()!, historyEntry.id!,
					JSON.stringify({func: historyEntry.operation, options: payload})
				);
			}

			portalDocument.setUpdated();
			this.updateGlobalDocumentList();
		} catch (e) {
			portalDocument?.unlock();
			this.updateGlobalDocumentList();
			throw e;
		}

		return portalDocument;
	}

	public async deleteDocument(documentId: string): Promise<void> {
		let portalDocument: PortalDocument = this.findDocument(documentId);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		documentSettings.selected = false;

		try {
			await super.deleteDocument(documentId);
		} catch (exception: any) {
			throw await this.getSession().handleException(exception);
		}

		let conditionalUpdates: Partial<PortalState> = {};
		if (this.getSelection().length === 0) {
			conditionalUpdates.contentMode = ContentMode.STARTSCREEN
		}

		this.updateGlobalDocumentList(conditionalUpdates);
	}

	public async deleteDocuments(documentIds: Array<string>): Promise<void> {
		for (let documentId of documentIds) {
			let portalDocument: PortalDocument = this.findDocument(documentId);
			let documentSettings: DocumentSettings = portalDocument.getSettings();
			documentSettings.selected = false;

			await super.deleteDocument(documentId);
		}

		let conditionalUpdates: Partial<PortalState> = {};
		if (this.getSelection().length === 0) {
			conditionalUpdates.contentMode = ContentMode.STARTSCREEN
		}

		this.updateGlobalDocumentList(conditionalUpdates);
	}

	/**
	 * Creates new {@link PortalDocument} instances
	 */
	protected createDocumentInstance(id: string): PortalDocument {
		return new PortalDocument(this._context, id);
	}

	/**
	 * downloads the raw data for the given document id if there is none
	 */
	public async downloadRawData(documentId: string): Promise<any> {
		let portalDocument: PortalDocument = this.findDocument(documentId);

		if (FunctionHelper.exists(portalDocument.data)) {
			return portalDocument.data;
		}

		let downloadFormat: string | undefined;
		if (portalDocument.hasHtmlPreview()) {
			downloadFormat = "HTML"
		}

		try {
			portalDocument.data = this.downloadDocument(documentId, {
				format: downloadFormat
			});
			portalDocument.data.then(async (responseData: HttpRestResponse): Promise<void> => {
				portalDocument.data = await responseData.getData();
			});
		} catch (exception: any) {
			throw await this.getSession().handleException(exception);
		}

		return portalDocument.data;
	}

	public set context(value: PortalContext) {
		this._context = value;
	}

	public async syncDocument(documentFile: DocumentFile): Promise<void> {
		let originalSettings: DocumentSettingsInterface | undefined;
		let portalDocument: PortalDocument;

		try {
			portalDocument = this.findDocument(documentFile.documentId);
			originalSettings = portalDocument.getSettings().toJson();
		} catch (e) {
			//can be ignored document does not exist yet
		}

		await super.syncDocument(documentFile);
		portalDocument = this.findDocument(documentFile.documentId);

		if (FunctionHelper.applyValue(documentFile.isFileLocked, false)) {
			portalDocument.lock();
		} else {
			if (FunctionHelper.exists(originalSettings)) {
				let documentSettings: DocumentSettings = portalDocument.getSettings();
				documentSettings.closed = originalSettings!.closed;
				documentSettings.selected = originalSettings!.selected;
			}

			await this.downloadRawData(documentFile.documentId);
			this.updateDocumentFormInfo(documentFile.documentId);
			portalDocument.unlock();
		}
	}

	public async sync(): Promise<void> {
		try {
			await super.sync();
		} catch (exception: any) {
			throw await this.getSession().handleException(exception);
		}

		this.updateGlobalDocumentList();
	}

	public getSession(): PortalSession {
		return super.getSession() as PortalSession;
	}

	public async renameDocument(
		documentId: string, fileName?: string, showMessage?: boolean
	): Promise<PortalDocument> {
		let portalDocument: PortalDocument = this.findDocument(documentId);
		portalDocument.lock();
		this.updateGlobalDocumentList();

		try {
			portalDocument = await super.renameDocument(documentId, fileName);
			portalDocument.unlock();
			this.updateGlobalDocumentList();
		} catch (exception: any) {
			portalDocument.unlock();
			this.updateGlobalDocumentList();
			throw await this.getSession().handleException(exception, showMessage);
		}

		return portalDocument;
	}

	public async updateSecurity(
		documentId: string, passwordType: PdfPassword, showMessage?: boolean
	): Promise<PortalDocument> {
		try {
			let portalDocument: PortalDocument = await super.updateSecurity(documentId, passwordType);
			portalDocument.setUpdated();
			this.updateGlobalDocumentList();

			this.updateDocumentFormInfo(portalDocument.getSourceDocumentId()!);

			return portalDocument;
		} catch (exception: any) {
			throw await this.getSession().handleException(exception, showMessage);
		}
	}

	public updateGlobalDocumentList(additionalUpdates?: any, callback?: () => void) {
		let portalDocuments: Array<PortalDocumentInterface> = [];
		let documentSelection: Array<PortalDocumentInterface> = [];

		for (let portalDocument of this.getDocuments()) {
			let documentInterface: PortalDocumentInterface = portalDocument.toJson();
			portalDocuments.push(documentInterface);

			if (!portalDocument.getSettings().selected) {
				continue;
			}

			documentSelection.push(documentInterface);
		}

		this._context.getPage().setState({
			...additionalUpdates,
			portalDocuments: Immutable.from(portalDocuments),
			documentSelection: Immutable.from(documentSelection)
		}, callback);
	}
}